import { Grid } from "@mui/material";
import ThemeContext from "components/app/Context";
import MultipleSelectCheckmarks from "components/common/dropdowns/MultiSelect";
import ButtonLink from "components/common/Link";
import Toggle from "components/common/Toggle";
import { isEmpty, uniq } from "lodash";
import { useContext } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import {
  includeAboveProperty,
  messages,
  minSearchStrLength,
  planTypes,
  roleCategoryList,
  sourceList,
  statusTypes
} from "utils/CommonConstants";

import SearchAppBar from "components/common/searchBar";
import SingleSelectFilter from "components/common/SingleSelectFilter";
import {
  converter_ArrayString,
  functionalAreaIdsToNames,
  functionalAreaNamesToIds,
  sourceValuetoName
} from "utils/CommonFunctions";
import { engagementTypes } from "utils/Types";
import { clearFilter, setAbovePropertyFilters, setSearchText } from "./AboveProperty.slice";

interface ToggleProp {
  toggleLabelOn: string;
  toggleLabelOff: string;
  isLabelLeftSide: boolean;
  isToggleEnabled: boolean;
}

interface Props {
  isSearchBarVisible?: boolean;
  isFunctionalAreaVisible?: boolean;
  isSourceVisible?: boolean;
  searchPredefinedValue?: string;
  isInitiativeTypeVisible?: boolean;
  parentView?: string;
  filterType?: string;
  isRoleVisible?: boolean;
  isPropertyVisible?: boolean;
  isRoleCategoryVisible?: boolean;
  isEngagementVisible?: boolean;
  searchBarPlaceHolder?: string;
  isInncodeVisible?: boolean;
  isBrandsVisible?: boolean;
  isStatusVisible?: boolean;
  isOwnersVisible?: boolean;
  isPlanTypesVisible?: boolean;
  optionsList?: any;
  isUserTypeToggleVisible?: boolean;
  toggleProp?: ToggleProp;
  isAssigneeVisible?: boolean;
  isPreDefinedNav?: boolean;
  minWidth?: string;
}

function FilterButtons(props: Props) {
  const dispatch = useAppDispatch();

  const {
    isSearchBarVisible = false,
    isFunctionalAreaVisible = false,
    isSourceVisible = false,
    isInitiativeTypeVisible = false,
    isRoleVisible = false,
    isPropertyVisible = false,
    isRoleCategoryVisible = false,
    isEngagementVisible = false,
    parentView = "planViewFilters",
    filterType = "assignedPlanFilters",
    searchBarPlaceHolder = "Search by plan name",
    isBrandsVisible = false,
    isStatusVisible = false,
    isOwnersVisible = false,
    isPlanTypesVisible = false,
    optionsList = {},
    isInncodeVisible = false,
    isUserTypeToggleVisible = false,
    isAssigneeVisible = false,
    toggleProp = { toggleLabelOn: "", toggleLabelOff: "", isLabelLeftSide: false, isToggleEnabled: false },
    isPreDefinedNav = false,
    searchPredefinedValue,
    minWidth = "240px"
  } = props;
  const functionalAreasList = useAppSelector((state: any) => state.functionalAreas?.list);
  const { isMobileScreen } = useContext(ThemeContext);
  const { propertyList, brands, initiativeFilterValues } = useAppSelector((state) => state.aboveProperty);
  const roles = useAppSelector((state) => state.app?.userRoles?.roles);
  const filters = useAppSelector((state: any) => state.aboveProperty.filters[parentView][filterType]);
  const isClearButton = Object.keys(filters).find((key) => {
    return Array.isArray(filters[key]) && filters[key]?.length > 0;
  });
  const brandsArray = brands?.filter((b: any) => !!b?.brandName)?.map((el: any) => el?.brandName);

  const onMultiSelectChange = (value: any, name: any) => {
    dispatch(
      setAbovePropertyFilters({
        parentView: parentView,
        filterType: filterType,
        filterName: name,
        value: value
      })
    );
  };

  const getInputDetails = (inputText: string) => {
    if (inputText?.length > minSearchStrLength || !inputText) {
      dispatch(setSearchText({ inputText, searchType: filterType, filterName: "searchText", parentView: parentView }));
    }
  };

  return (
    <Grid container padding={isMobileScreen ? "20px 3px" : "10px 0 0"} alignItems={"center"}>
      <Grid item sm={3} xs={12}>
        {isSearchBarVisible && (
          <SearchAppBar
            value={searchPredefinedValue || filters["searchText"]}
            getInputDetails={getInputDetails}
            placeholder={searchBarPlaceHolder}
            sx={{ minWidth, border: `solid 1px ${tokens.Border.General.Tertiary}` }}
            isPreDefinedNav={isPreDefinedNav}
          />
        )}
      </Grid>

      <Grid
        item
        sm={true}
        xs={12}
        flexWrap="wrap"
        display="flex"
        alignItems={"center"}
        sx={{
          justifyContent: { xs: filterType === "templatePlans" ? "flex-end" : "space-between", md: "flex-end" },
          marginTop: { xs: "8px" },
          marginBottom: { xs: "8px" }
        }}
        marginLeft="auto"
      >
        {isUserTypeToggleVisible && (
          <Toggle
            labelOn={toggleProp?.toggleLabelOn}
            labelOff={toggleProp?.toggleLabelOff}
            isToggleEnabled={toggleProp?.isToggleEnabled}
            onChange={(value: boolean) => onMultiSelectChange(value, includeAboveProperty)}
            isLabelLeftSide={toggleProp?.isLabelLeftSide}
            sx={{ marginLeft: "8px" }}
          />
        )}
        {isClearButton && filterType !== "templatePlans" && (
          <Grid
            item
            xs={12}
            md={2}
            display="flex"
            justifyContent={isMobileScreen ? "flex-start" : "flex-end"}
            marginLeft={isMobileScreen ? "8px" : ""}
          >
            <ButtonLink
              text="Clear all"
              sx={{ color: tokens.Text.General.Color }}
              textVariant="body2"
              underline="none"
              onClick={() => {
                dispatch(clearFilter({ parentView: parentView, filterType: filterType, isClearAll: true }));
              }}
            />
          </Grid>
        )}

        {isFunctionalAreaVisible && (
          <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
            {isClearButton && filterType === "templatePlans" && (
              <Grid
                item
                xs={12}
                md={3}
                display="flex"
                justifyContent={isMobileScreen ? "flex-start" : "flex-end"}
                marginLeft={isMobileScreen ? "8px" : ""}
              >
                <ButtonLink
                  text="Clear all"
                  sx={{ color: tokens.Text.General.Color }}
                  textVariant="body2"
                  underline="none"
                  onClick={() => {
                    dispatch(clearFilter({ parentView: parentView, filterType: filterType, isClearAll: true }));
                  }}
                />
              </Grid>
            )}
            <MultipleSelectCheckmarks
              minWidth={isMobileScreen ? "160px" : "150px"}
              name="functionalAreas"
              label="Functional Area"
              options={functionalAreasList}
              initialValues={functionalAreaIdsToNames(filters["functionalAreas"])}
              onChange={(value: any) => onMultiSelectChange(functionalAreaNamesToIds(value), "functionalAreas")}
            ></MultipleSelectCheckmarks>
          </div>
        )}

        {isSourceVisible && (
          <MultipleSelectCheckmarks
            minWidth={isMobileScreen ? "160px" : "90px"}
            name="source"
            label="Source"
            options={sourceList}
            initialValues={sourceValuetoName(filters["source"])}
            onChange={(value: any) =>
              onMultiSelectChange(
                [].concat(
                  ...(value || [])
                    .map((item: any) => {
                      const foundType = sourceList.find((type: any) => type.name === item);
                      return foundType ? foundType.value : null;
                    })
                    .filter(Boolean)
                ),
                "source"
              )
            }
          ></MultipleSelectCheckmarks>
        )}
        {isInitiativeTypeVisible && (
          <MultipleSelectCheckmarks
            minWidth={isMobileScreen ? "160px" : "110px"}
            name="initiatives"
            label="Initiative"
            options={initiativeFilterValues}
            initialValues={filters["initiatives"]}
            onChange={onMultiSelectChange}
          ></MultipleSelectCheckmarks>
        )}
        {isRoleVisible && (
          <MultipleSelectCheckmarks
            name="role"
            label="Role"
            minWidth={isMobileScreen ? "160px" : "75px"}
            options={converter_ArrayString(uniq(roles?.map((el: any) => el?.roleName)), "name")}
            initialValues={filters["role"]}
            onChange={onMultiSelectChange}
          ></MultipleSelectCheckmarks>
        )}
        {isPropertyVisible && (
          <MultipleSelectCheckmarks
            name="property"
            label="Property"
            minWidth={isMobileScreen ? "160px" : "150px"}
            options={converter_ArrayString(propertyList?.map((el: any) => el?.innCode).sort(), "name")}
            initialValues={filters["property"]}
            onChange={(value: any) => onMultiSelectChange(value, "property")}
            showInncode={true}
          ></MultipleSelectCheckmarks>
        )}

        {isRoleCategoryVisible && (
          <SingleSelectFilter
            label="Role Category"
            options={converter_ArrayString(roleCategoryList, "name")}
            showTextField={false}
            optionKeyName="name"
            onSubmit={(val: any) => onMultiSelectChange([val], "roleCategory")}
            selectedOption={filters["roleCategory"]}
            width={isMobileScreen ? "160px" : "150px"}
          />
        )}

        {isBrandsVisible && (
          <MultipleSelectCheckmarks
            name="brandCodes"
            label="Brand"
            options={converter_ArrayString(brandsArray, "name")}
            onChange={(v: any) => onMultiSelectChange(v, "brandCodes")}
            minWidth={isMobileScreen ? "160px" : "80px"}
            initialValues={filters["brandCodes"]}
          ></MultipleSelectCheckmarks>
        )}
        {isEngagementVisible && (
          <MultipleSelectCheckmarks
            name="engagements"
            label="Engagement"
            options={converter_ArrayString(
              !isEmpty(optionsList) && !isEmpty(optionsList?.engagements)
                ? Object.values(optionsList?.engagements)
                : Object.values(engagementTypes),
              "name"
            )}
            onChange={(v: any) => onMultiSelectChange(v, "engagements")}
            minWidth={isMobileScreen ? "160px" : "130px"}
            initialValues={filters["engagements"]}
          ></MultipleSelectCheckmarks>
        )}
        {isInncodeVisible && (
          <MultipleSelectCheckmarks
            name="inncode"
            label="Inncode"
            minWidth={isMobileScreen ? "160px" : "100px"}
            initialValues={filters["inncode"]}
            options={converter_ArrayString(optionsList?.inncode, "name")}
            onChange={(v: any) => onMultiSelectChange(v, "inncode")}
          ></MultipleSelectCheckmarks>
        )}
        {isStatusVisible && (
          <MultipleSelectCheckmarks
            name="status"
            label="Status"
            options={converter_ArrayString(statusTypes, "name")}
            onChange={(v: any) => onMultiSelectChange(v, "status")}
            minWidth={isMobileScreen ? "160px" : "80px"}
            initialValues={filters["status"]}
          ></MultipleSelectCheckmarks>
        )}
        {isOwnersVisible && (
          <SingleSelectFilter
            label="Owner"
            options={optionsList?.owner}
            optionKeyName="displayName"
            onSubmit={(val) => {
              onMultiSelectChange([val], "owners");
            }}
            selectedOption={filters["owners"]}
            width={isMobileScreen ? "160px" : "130px"}
            showTextField={false}
            noOptionsMessage={messages.noOwners}
          />
        )}
        {isAssigneeVisible && (
          <SingleSelectFilter
            label="Assignee"
            options={optionsList?.assignee}
            optionKeyName="displayName"
            onSubmit={(val) => {
              onMultiSelectChange([val], "owners");
            }}
            selectedOption={filters["owners"]}
            width={isMobileScreen ? "160px" : "130px"}
            showTextField={false}
            noOptionsMessage={messages.noAssignee}
          />
        )}
        {isPlanTypesVisible && (
          <MultipleSelectCheckmarks
            name="planType"
            label="Plan Type"
            options={planTypes?.map((o: any) => ({ name: o?.planTypeName }))}
            onChange={(v: any) => onMultiSelectChange(v, "planType")}
            minWidth={isMobileScreen ? "160px" : "130px"}
            initialValues={filters["planType"]}
          ></MultipleSelectCheckmarks>
        )}
      </Grid>
    </Grid>
  );
}

export default FilterButtons;
